import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { useHistory } from 'react-router';
import qs from 'query-string';

import { PaymentCardSortBy, SortOrder } from '__generated__/GQL';

import { CardsView } from '../../types';
import { Action, paymentCardListFiltersReducer, State } from './reducer';

type Context = State & {
  dispatch: Dispatch<Action>;
};

interface PaymentCardsListFiltersProviderProps {
  children: ReactNode;
  canViewCompanyCards: boolean;
}

const DEFAULT_STATUS = ['LOCKED', 'ACTIVE', 'INACTIVE', 'CREATED'];
const DEFAULT_SORT_BY = PaymentCardSortBy.Status;
const DEFAULT_ORDER_BY = SortOrder.Asc;

const PaymentCardsListFiltersContext = createContext<Context | undefined>(
  undefined,
);

export const usePaymentCardsListState = (): Context => {
  const context = useContext(PaymentCardsListFiltersContext);

  if (!context) {
    throw new Error(
      'usePaymentCardListState must be used within a <PaymentCardsListFiltersProvider/>',
    );
  }

  return context;
};

const queryToFilters = (
  search: string,
  { canViewCompanyCards }: { canViewCompanyCards: boolean },
): State => {
  const { cardProductType, orderBy, owner, sortBy, status, view } = qs.parse(
    search,
    {
      arrayFormat: 'bracket',
    },
  );

  const getView = () => {
    if (!canViewCompanyCards) {
      return CardsView.OWN;
    }

    return view === CardsView.COMPANY ? CardsView.COMPANY : CardsView.OWN;
  };

  const getSortBy = () => {
    if (
      typeof sortBy === 'string' &&
      Object.values(PaymentCardSortBy).includes(sortBy as PaymentCardSortBy)
    ) {
      return sortBy as PaymentCardSortBy;
    }

    return DEFAULT_SORT_BY;
  };

  const getOrderBy = (): SortOrder => {
    if (
      typeof orderBy === 'string' &&
      Object.values(SortOrder).includes(orderBy as SortOrder)
    ) {
      return orderBy as SortOrder;
    }

    return DEFAULT_ORDER_BY;
  };

  const currentOrderBy = getOrderBy();
  const currentSortBy = getSortBy();

  return {
    canViewCompanyCards,
    filters: {
      cardProductType: Array.isArray(cardProductType) ? cardProductType : [],
      owner: Array.isArray(owner) ? owner : [],
      status: Array.isArray(status) ? status : DEFAULT_STATUS,
    },
    ordering: {
      [PaymentCardSortBy.OwnerName]:
        currentSortBy === PaymentCardSortBy.OwnerName
          ? currentOrderBy
          : SortOrder.Desc,
      [PaymentCardSortBy.LimitPayment]:
        currentSortBy === PaymentCardSortBy.LimitPayment
          ? currentOrderBy
          : SortOrder.Desc,
      [PaymentCardSortBy.Status]:
        currentSortBy === PaymentCardSortBy.Status
          ? currentOrderBy
          : SortOrder.Desc,
      currentOrdering: {
        orderBy: currentOrderBy,
        sortBy: currentSortBy,
      },
    },
    view: getView(),
  };
};

export const PaymentCardsListFiltersProvider: FC<
  PaymentCardsListFiltersProviderProps
> = ({ canViewCompanyCards, children }) => {
  const history = useHistory();
  const [state, dispatch] = useReducer(
    paymentCardListFiltersReducer,
    queryToFilters(history.location.search, { canViewCompanyCards }),
  );

  useEffect(() => {
    history.push({
      search: qs.stringify(
        {
          ...state.filters,
          orderBy: state.ordering.currentOrdering.orderBy,
          sortBy: state.ordering.currentOrdering.sortBy,
          view: state.view,
        },
        {
          arrayFormat: 'bracket',
          skipEmptyString: true,
          skipNull: true,
        },
      ),
    });
  }, [history, state.view, state.filters, state.ordering.currentOrdering]);

  const context = {
    ...state,
    dispatch,
  } satisfies Context;

  return (
    <PaymentCardsListFiltersContext.Provider value={context}>
      {children}
    </PaymentCardsListFiltersContext.Provider>
  );
};
